import { ArrowDown, ArrowUp } from "lucide-react";
import React, { useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { BucketContext, ParsedFileInfo } from "../../../../types/blob";
import EmptyState from "./EmptyState";
import FileRow from "./FileRow";

type SortColumn = "name" | "size" | "contentType" | "updated";
type SortDirection = "asc" | "desc";

interface FileTableProps {
  files: ParsedFileInfo[];
  context: BucketContext;
  loading: boolean;
  onNavigateToFolder: (folderName: string) => void;
  onPreview: (file: ParsedFileInfo) => void;
  onDownload: (file: ParsedFileInfo) => void;
  onDelete: (file: ParsedFileInfo) => void;
  onCopyUrl: (file: ParsedFileInfo) => void;
}

const FileTable: React.FC<FileTableProps> = ({
  files,
  context,
  loading,
  onNavigateToFolder,
  onPreview,
  onDownload,
  onDelete,
  onCopyUrl,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [sortColumn, setSortColumn] = useState<SortColumn>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedFiles = [...files].sort((a, b) => {
    let comparison = 0;

    switch (sortColumn) {
      case "name":
        comparison = a.displayName.localeCompare(b.displayName);
        break;
      case "size":
        comparison = a.size - b.size;
        break;
      case "contentType":
        comparison = a.contentType.localeCompare(b.contentType);
        break;
      case "updated":
        comparison =
          new Date(a.updated).getTime() - new Date(b.updated).getTime();
        break;
    }

    return sortDirection === "asc" ? comparison : -comparison;
  });

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? (
      <ArrowUp className="w-3 h-3 ml-1" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1" />
    );
  };

  const headerClass = `px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none ${
    isDarkMode
      ? "text-rp-grey/50 hover:text-rp-grey"
      : "text-gray-500 hover:text-gray-700"
  }`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (files.length === 0) {
    return <EmptyState />;
  }

  return (
    <div>
      <table
        className={`w-full divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}
      >
        <thead className={isDarkMode ? "bg-rp-surface" : "bg-gray-50"}>
          <tr>
            <th
              className={headerClass}
              onClick={() => handleSort("name")}
            >
              <div className="flex items-center">
                Name
                <SortIcon column="name" />
              </div>
            </th>
            <th
              className={headerClass}
              onClick={() => handleSort("size")}
            >
              <div className="flex items-center">
                Size
                <SortIcon column="size" />
              </div>
            </th>
            <th
              className={headerClass}
              onClick={() => handleSort("contentType")}
            >
              <div className="flex items-center">
                Type
                <SortIcon column="contentType" />
              </div>
            </th>
            <th
              className={headerClass}
              onClick={() => handleSort("updated")}
            >
              <div className="flex items-center">
                Modified
                <SortIcon column="updated" />
              </div>
            </th>
            <th
              className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                isDarkMode ? "text-rp-grey/50" : "text-gray-500"
              }`}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody
          className={`${isDarkMode ? "bg-rp-surface divide-rp-separator" : "bg-white divide-gray-200"} divide-y`}
        >
          {sortedFiles.map((file, index) => (
            <FileRow
              key={file.name}
              file={file}
              context={context}
              isFolder={file.isFolder}
              onNavigate={() => onNavigateToFolder(file.displayName)}
              onPreview={() => onPreview(file)}
              onDownload={() => onDownload(file)}
              onDelete={() => onDelete(file)}
              onCopyUrl={() => onCopyUrl(file)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FileTable;

